import React, { useState } from 'react';
import styled from 'styled-components';
import { FaGithub, FaExternalLinkAlt, FaBriefcase } from 'react-icons/fa';

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--secondary-color);
    margin: 0.5rem auto 0;
  }
`;

const ProjectsSummary = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-size: 1.05rem;
  color: var(--dark-gray);
`;

const ProjectFilters = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FilterButton = styled.button`
  background: ${props => props.$active ? 'var(--secondary-color)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-color)'};
  border: 1px solid ${props => props.$active ? 'var(--secondary-color)' : 'var(--border-color)'};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.$active ? 'var(--secondary-color)' : 'var(--light-gray)'};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: var(--white);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  box-shadow: none;
  transition: var(--transition);
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-sm);
  }
`;

const ProjectHeader = styled.div`
  height: 8px;
  background: linear-gradient(90deg, #2563eb 0%, #60a5fa 100%);
`;

const ProjectContent = styled.div`
  padding: 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProjectBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background-color: ${props => props.$professional ? '#eff6ff' : '#f0fdf4'};
  color: ${props => props.$professional ? '#2563eb' : '#16a34a'};
  border: 1px solid ${props => props.$professional ? '#bfdbfe' : '#bbf7d0'};
`;

const ProjectTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0;
`;

const ProjectDescription = styled.p`
  font-size: 0.975rem;
  color: var(--dark-gray);
  line-height: 1.65;
  flex: 1;
`;

const ProjectTechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const TechTag = styled.span`
  background-color: #f1f5f9;
  color: #334155;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  border: 1px solid var(--border-color);
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
`;

const ProjectLink = styled.a`
  color: var(--dark-gray);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: var(--transition);
  
  &:hover {
    color: var(--secondary-color);
  }
`;

const NDA = styled.span`
  color: var(--dark-gray);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-style: italic;
`;

const Projects = () => {
  const [filter, setFilter] = useState('all');
  
  const projects = [
    {
      id: 1,
      title: 'E-Commerce Platform',
      type: 'personal',
      description: 'Full-stack e-commerce application built with Spring Boot and Next.js. Features user authentication, product catalog management, shopping cart, order processing, and image handling.',
      techStack: ['Java', 'Spring Boot', 'Next.js', 'PostgreSQL', 'Docker', 'GitHub Actions'],
      github: 'https://github.com/sultanmalik04/spring-shop',
      category: 'fullstack',
    },
    {
      id: 2,
      title: 'FTA E-Invoicing Compliance SDK',
      type: 'professional',
      description: 'Java & Python SDKs connecting ERP systems (Sage X3, SAP, Oracle) to UAE FTA e-invoicing gateways. Adopted by 20+ enterprise clients, enabling compliant e-invoice exchange with 99.9%+ uptime.',
      techStack: ['Java', 'Python', 'Fast API', 'Spring Boot', 'OAuth 2.0', 'JWT', 'RabbitMQ', 'AWS'],
      category: 'integration',
    },
    {
      id: 3,
      title: 'Multi-Tenant Banking Middleware',
      type: 'professional',
      description: 'Productised middleware platform with pluggable connectors and a unified API for Indian banking services. Reduced go-live time from weeks to days and enabled 8+ teams to integrate without re-implementation.',
      techStack: ['Java', 'Spring Boot', 'Oracle SQL', 'Docker', 'Weblogic', 'REST APIs'],
      category: 'enterprise',
    },
    {
      id: 4,
      title: 'Bank Statement AI Analyser Integration',
      type: 'professional',
      description: 'End-to-end integration of the Perfios AI/ML Bank Statement Analyser at Bank of Baroda, replacing manual verification with a real-time automated workflow. Reduced credit processing from hours to minutes.',
      techStack: ['Java', 'Spring Boot', 'Spring Integration', 'Oracle SQL', 'REST APIs'],
      category: 'integration',
    },
    {
      id: 5,
      title: 'Islamic Prayer Routine App',
      type: 'professional',
      description: 'Full-stack mobile application supporting daily Islamic prayer routines. Features location-aware prayer times, Qibla compass, prayer tracker with streak metrics, and Google Maps mosque discovery.',
      techStack: ['Flutter', 'Firebase', 'Google Maps API', 'GPS APIs', 'Google Sign-In'],
      category: 'mobile',
    },
  ];
  
  const filters = [
    { label: 'All', value: 'all' },
    { label: 'Fullstack', value: 'fullstack' },
    { label: 'Enterprise', value: 'enterprise' },
    { label: 'Integration', value: 'integration' },
    { label: 'Mobile', value: 'mobile' },
  ];

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.category === filter);
  
  return (
    <ProjectsContainer>
      <SectionTitle>Projects</SectionTitle>
      
      <ProjectsSummary>
        A mix of personal side projects and significant professional work. Enterprise projects are under NDA — descriptions reflect publicly shareable detail.
      </ProjectsSummary>
      
      <ProjectFilters>
        {filters.map(f => (
          <FilterButton
            key={f.value}
            $active={filter === f.value}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </FilterButton>
        ))}
      </ProjectFilters>
      
      <ProjectsGrid>
        {filteredProjects.map(project => (
          <ProjectCard key={project.id}>
            <ProjectHeader />
            <ProjectContent>
              <ProjectMeta>
                <ProjectBadge $professional={project.type === 'professional'}>
                  {project.type === 'professional' ? 'Professional' : 'Personal'}
                </ProjectBadge>
              </ProjectMeta>

              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              
              <ProjectTechStack>
                {project.techStack.map(tech => (
                  <TechTag key={tech}>{tech}</TechTag>
                ))}
              </ProjectTechStack>

              <ProjectLinks>
                {project.github ? (
                  <ProjectLink href={project.github} target="_blank" rel="noopener noreferrer">
                    <FaGithub /> View on GitHub
                  </ProjectLink>
                ) : (
                  <NDA><FaBriefcase /> Enterprise / NDA</NDA>
                )}
                {project.live && (
                  <ProjectLink href={project.live} target="_blank" rel="noopener noreferrer">
                    <FaExternalLinkAlt /> Live Demo
                  </ProjectLink>
                )}
              </ProjectLinks>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
    </ProjectsContainer>
  );
};

export default Projects;
